import { getPreviousData, type GetPreviousDataReturn } from '@/dashboards/shared/optimisation/previousData';
import { getDatesConfig } from '@/dashboards/CampaignOptimization/helpers/getDatesConfig';
import { useModelContribution } from '@/dashboards/ModelContributions';
import type { GetModelContributionResponse } from '@/dashboards/ModelContributions/types';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { useDuckDBQuery } from '@/duckdb';
import { keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';

export type ContributionLastYear = {
    data: GetModelContributionResponse;
    isLoading: boolean;
    isFetching: boolean;
};

const EMPTY_CONTRIBUTION: GetModelContributionResponse = { rows: [] } as GetModelContributionResponse;

export const useCampaignHistoricalData = ({
    startDate,
    endDate,
    enabled,
    graphPeriod,
}: {
    startDate: string | undefined;
    endDate: string | undefined;
    enabled: boolean;
    graphPeriod: 'monthly' | 'weekly';
}): {
    previous: GetPreviousDataReturn | undefined;
    isPreviousLoading: boolean;
    isPreviousFetching: boolean;
    contributionLastYear: ContributionLastYear;
} => {
    const {
        project: { interval },
    } = useDashboardGlobalContext();

    const { prevMinDate, prevMaxDate, prevYearEndDate, prevYearStartDate } = useMemo(
        () => getDatesConfig(startDate, endDate),
        [startDate, endDate],
    );

    const previous = useDuckDBQuery({
        queryKey: ['campaign-previous-data', interval, startDate, endDate],
        queryFn: () =>
            getPreviousData({
                endDate: prevYearEndDate,
                startDate: prevMinDate,
                interval: interval as 'weekly' | 'daily',
            }),
        enabled: enabled && !!startDate && !!endDate,
        placeholderData: keepPreviousData,
    });

    const lastYear = useModelContribution(
        { period: graphPeriod, endDate: prevMaxDate, startDate: prevYearStartDate },
        { enabled },
    );

    const contributionLastYear = useMemo<ContributionLastYear>(
        () => ({
            data: lastYear.data ?? EMPTY_CONTRIBUTION,
            isLoading: lastYear.isLoading,
            isFetching: lastYear.isFetching,
        }),
        [lastYear.data, lastYear.isLoading, lastYear.isFetching],
    );

    return {
        previous: previous.data,
        isPreviousLoading: previous.isLoading,
        isPreviousFetching: previous.isFetching,
        contributionLastYear,
    };
};
